import type { TFunction } from "i18next";
import { Bell, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import type { DataTableColumn } from "../../../../components/table";
import type { DataTableOption } from "../../../../components/table";
import { Button } from "../../../../components/button";
import { AppTooltip } from "../../../../components/tooltip";
import type {
  NotificationItem,
  NotificationSortDirection,
  NotificationSortKey,
} from "../../types/notification.types";

export type NotificationTableFilters = {
  title: string;
  content: string;
  alertTypeId: string;
};

type BuildNotificationTableColumnsParams = {
  t: TFunction;
  filters: NotificationTableFilters;
  alertTypeOptions: DataTableOption[];
  sortKey: NotificationSortKey | null;
  sortDirection: NotificationSortDirection | null;
  onFilterChange: (key: keyof NotificationTableFilters, value: string) => void;
  onSort: (key: NotificationSortKey, direction: NotificationSortDirection | null) => void;
  updatingIds: ReadonlySet<string>;
  formatDate: (date: string) => string;
  isOlderThan24Hours: (date: string) => boolean;
  onEdit: (notification: Pick<NotificationItem, "id" | "title">) => void;
  onPinToggle: (notification: NotificationItem) => void | Promise<void>;
  onDelete: (notification: Pick<NotificationItem, "id" | "title">) => void;
};

export const buildNotificationTableColumns = ({
  t,
  filters,
  alertTypeOptions,
  sortKey,
  sortDirection,
  onFilterChange,
  onSort,
  updatingIds,
  formatDate,
  isOlderThan24Hours,
  onEdit,
  onPinToggle,
  onDelete,
}: BuildNotificationTableColumnsParams): DataTableColumn<NotificationItem>[] => [
  {
    key: "icon",
    title: "Icon",
    align: "center",
    width: 80,
    render: (notification) => (
      <div className="flex justify-center">
        {notification.thumbnail_url ? (
          <img
            src={notification.thumbnail_url}
            alt={notification.title}
            className="h-12 w-12 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
            {notification.alert_type?.icon ? (
              <span className="text-lg">{notification.alert_type.icon}</span>
            ) : (
              <Bell className="h-4 w-4 text-gray-400" />
            )}
          </div>
        )}
      </div>
    ),
  },
  {
    key: "title",
    title: t("notification.title"),
    filter: {
      type: "string",
      value: filters.title,
      onFilter: (value) => onFilterChange("title", value.trim()),
      placeholder: `Search ${t("notification.title").toLowerCase()}...`,
      title: `FILTER BY ${t("notification.title").toUpperCase()}`,
      description: "Press Enter to search",
      submitMode: "enter",
    },
    cellClassName: "min-w-[180px] font-medium",
    render: (notification) => (
      <div className="flex items-center gap-2">
        {notification.is_pinned ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
            aria-label={t("notification.pinned")}
          >
            <Pin className="h-3 w-3" aria-hidden="true" />
            {t("notification.pinned")}
          </span>
        ) : null}
        <span className="truncate">{notification.title}</span>
      </div>
    ),
  },
  {
    key: "content",
    title: t("notification.content"),
    filter: {
      type: "string",
      value: filters.content,
      onFilter: (value) => onFilterChange("content", value.trim()),
      placeholder: `Search ${t("notification.content").toLowerCase()}...`,
      title: `FILTER BY ${t("notification.content").toUpperCase()}`,
      description: "Press Enter to search",
      submitMode: "enter",
    },
    cellClassName: "min-w-[280px] max-w-md",
    render: (notification) => (
      <div className="line-clamp-2 text-sm text-muted-foreground">{notification.content}</div>
    ),
  },
  {
    key: "alert_type",
    title: t("notification.alert_type"),
    filter: {
      type: "dropdown",
      value: filters.alertTypeId,
      onFilter: (value) => onFilterChange("alertTypeId", value),
      options: alertTypeOptions,
      title: `FILTER BY ${t("notification.alert_type").toUpperCase()}`,
      placeholder: t("notification.select_alert_type"),
      clearLabel: "Clear",
    },
    cellClassName: "min-w-[140px] font-medium",
    render: (notification) => notification.alert_type?.name || "-",
  },
  {
    key: "created_at",
    title: t("notification.created_at"),
    sort: {
      direction: sortKey === "created_at" ? sortDirection : null,
      onSort: (direction) => onSort("created_at", direction),
      title: t("notification.created_at"),
    },
    cellClassName: "min-w-[140px] text-xs",
    render: (notification) => formatDate(notification.created_at),
  },
  {
    key: "updated_at",
    title: t("notification.updated_at"),
    sort: {
      direction: sortKey === "updated_at" ? sortDirection : null,
      onSort: (direction) => onSort("updated_at", direction),
      title: t("notification.updated_at"),
    },
    cellClassName: "min-w-[140px] text-xs",
    render: (notification) => formatDate(notification.updated_at),
  },
  {
    key: "actions",
    title: t("common.actions"),
    cellClassName: "min-w-[110px]",
    render: (notification) => {
      const isUpdating = updatingIds.has(notification.id);
      const isTooOldToPin = isOlderThan24Hours(notification.created_at) && !notification.is_pinned;

      const pinTooltip = isTooOldToPin
        ? t("notification.pin_disabled_tooltip")
        : notification.is_pinned
          ? t("notification.unpin_notification")
          : t("notification.pin_notification");

      return (
        <div
          className="flex gap-2"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <AppTooltip title={t("common.edit")}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-8 rounded-md border-gray-300 bg-white p-0 hover:bg-gray-50"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(notification);
              }}
              aria-label={`Edit ${notification.title}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </AppTooltip>
          <AppTooltip title={pinTooltip}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-8 rounded-md border-gray-300 bg-white p-0 hover:bg-gray-50"
              title={pinTooltip}
              onClick={(event) => {
                event.stopPropagation();
                void onPinToggle(notification);
              }}
              disabled={isUpdating || isTooOldToPin}
              style={
                notification.is_pinned
                  ? { color: "#059669" }
                  : isTooOldToPin
                    ? { color: "#9CA3AF" }
                    : { color: "#4B5563" }
              }
              aria-label={notification.is_pinned ? "Unpin notification" : "Pin notification"}
            >
              {isUpdating ? (
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
              ) : notification.is_pinned ? (
                <Pin className="h-4 w-4" />
              ) : (
                <PinOff className="h-4 w-4" />
              )}
            </Button>
          </AppTooltip>
          <AppTooltip title={t("common.delete")}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-8 rounded-md border-gray-300 bg-white p-0 text-red-600 hover:bg-gray-50 hover:text-red-700 dark:border-gray-800 dark:bg-gray-500 dark:text-red-700 dark:hover:bg-gray-700"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(notification);
              }}
              aria-label={`Delete ${notification.title}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AppTooltip>
        </div>
      );
    },
  },
];
